/**
 * TradeSubTable component for displaying trade history within an expanded position row.
 */

import { useMemo } from 'react';
import type { TradeRecord } from '@/types/position';
import { sortTrades, getTradeSourceDisplay } from '@/utils/tradeHelpers';
import { formatCurrency, formatDate } from '@/utils/formatters';

interface TradeSubTableProps {
  /** Trades to display */
  trades: TradeRecord[];
  /** Position symbol for aria-label */
  symbol: string;
  /** Trade source from the position (may be null/undefined) */
  tradeSource?: string | null;
}

/**
 * Renders a nested trade history table for an expanded position row.
 * Displays trade action, quantity, price, total cost, date, and trade source.
 */
export function TradeSubTable({ trades, symbol, tradeSource }: TradeSubTableProps) {
  const sortedTrades = useMemo(() => sortTrades(trades ?? []), [trades]);

  if (sortedTrades.length === 0) {
    return (
      <div className="bg-gray-800 pl-8 py-4">
        <p className="text-sm text-gray-400">No trade history available</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 pl-8 py-2">
      <table
        className="w-full text-left text-sm"
        aria-label={`Trade history for ${symbol}`}
      >
        <thead>
          <tr className="text-xs text-gray-400 border-b border-gray-700">
            <th className="py-2 pr-4 font-medium">Action</th>
            <th className="py-2 pr-4 font-medium">Quantity</th>
            <th className="py-2 pr-4 font-medium">Price</th>
            <th className="py-2 pr-4 font-medium">Total Cost</th>
            <th className="py-2 pr-4 font-medium">Date</th>
            <th className="py-2 pr-4 font-medium">Trade Source</th>
          </tr>
        </thead>
        <tbody>
          {sortedTrades.map((trade) => (
            <tr key={trade.trade_id} className="border-b border-gray-700/50 text-gray-200">
              <td className="py-2 pr-4">{trade.action}</td>
              <td className="py-2 pr-4">{trade.quantity.toLocaleString()}</td>
              <td className="py-2 pr-4">{formatCurrency(trade.price)}</td>
              <td className="py-2 pr-4">{formatCurrency(trade.total_cost)}</td>
              <td className="py-2 pr-4">{formatDate(trade.timestamp)}</td>
              <td className="py-2 pr-4">{getTradeSourceDisplay(tradeSource)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
